import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Package, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatEUR } from "@/lib/format";

const STATUS_LABEL = {
  completed: { t: "Payée", c: "text-green-400 bg-green-500/10" },
  initiated: { t: "En attente", c: "text-amber-400 bg-amber-500/10" },
  failed: { t: "Échouée", c: "text-red-400 bg-red-500/10" },
  expired: { t: "Expirée", c: "text-stone-400 bg-stone-500/10" },
};

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user === false) navigate("/connexion");
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.email) {
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    }
  }, [user]);

  if (!user || user === false) {
    return <div className="min-h-[50vh] flex items-center justify-center text-stone-500">Chargement…</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12 lg:py-16" data-testid="account-page">
      <div className="flex items-center justify-between mb-10">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <User className="h-7 w-7 text-amber-500" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-black text-stone-100">Bonjour, {user.name}</h1>
            <p className="text-stone-400 text-sm">{user.email}</p>
          </div>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-amber-900/40 text-stone-300 hover:text-red-400 hover:border-red-500/40 text-sm font-semibold" data-testid="account-logout">
          <LogOut className="h-4 w-4" /> Déconnexion
        </button>
      </div>

      <div className="gothic-card rounded-2xl p-6">
        <h2 className="font-display text-xl font-bold text-stone-100 mb-5 flex items-center gap-2">
          <Package className="h-5 w-5 text-amber-500" /> Mes commandes
        </h2>
        {orders.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-stone-400 mb-5">Vous n'avez pas encore de commande.</p>
            <Link to="/boutique" className="btn-ember rounded-xl px-6 py-3 inline-block">Découvrir la boutique</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => {
              const s = STATUS_LABEL[o.status] || STATUS_LABEL.initiated;
              return (
                <div key={o.session_id} className="flex items-center justify-between gothic-card rounded-xl p-4" data-testid="order-row">
                  <div>
                    <p className="font-mono-tech text-sm text-stone-300">#{o.session_id.slice(-10)}</p>
                    <p className="text-xs text-stone-500">
                      {o.created_at ? new Date(o.created_at).toLocaleDateString("fr-FR") : ""} · {o.items?.length || 0} article(s)
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.c}`}>{s.t}</span>
                    <span className="font-mono-tech text-amber-400 font-bold">{formatEUR(o.amount)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
